import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Barcode, ChevronDown, LoaderCircle, Mic, PackageX, Search, Sparkles } from 'lucide-react';

import { api, ApiError } from '@/api/client';
import { useBarcodeLookup, isBarcodeNotFound } from '@/api/hooks/useBarcodeLookup';
import { useBillingCatalogue, useFrequentlySold } from '@/api/hooks/useMedicine';
import { useSubstitutes } from '@/api/hooks/useSubstitutes';
import type { Medicine, MedicineSearchIndex } from '@/api/types';
import { cartActions } from '@/features/billing/cartStore';
import { searchMedicines } from '@/features/billing/searchMedicines';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { cn, money } from '@/lib/utils';

// A USB/Bluetooth barcode scanner types the code and an Enter, faster than any
// person can — most scans land well under 30ms between characters. That's the
// signal used to tell "someone scanned a barcode" apart from "someone typed
// a name that happens to be all digits".
const SCANNER_KEYSTROKE_MS = 30;
const SCANNER_MIN_LENGTH = 6;

/**
 * The slim search-index rows carry `expiryDate`; the full Medicine DTO
 * (barcode lookup, substitutes) carries a server-computed `daysToExpiry`.
 * This accepts either so every add path behaves identically.
 */
type CartableMedicine = MedicineSearchIndex | Medicine;

function daysToExpiryOf(medicine: CartableMedicine): number {
  if ('daysToExpiry' in medicine && typeof medicine.daysToExpiry === 'number') {
    return medicine.daysToExpiry;
  }
  const expiry = (medicine as MedicineSearchIndex).expiryDate;
  if (!expiry) return 9999;
  const diff = new Date(`${expiry}T00:00:00`).getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(diff / 86_400_000);
}

function addMedicineToCart(medicine: CartableMedicine) {
  const added = cartActions.addItem({
    medicineId: medicine.id,
    name: medicine.name,
    shortName: medicine.shortName ?? undefined,
    price: medicine.price,
    unit: medicine.unit,
    maxStock: medicine.stock,
    batchNo: medicine.batchNo,
    daysToExpiry: daysToExpiryOf(medicine),
    gstRate: medicine.gstRate,
  });

  if (!added) {
    toast({
      title: 'Already at max stock',
      description: `Only ${medicine.stock} ${medicine.unit} of ${medicine.name} in stock.`,
      variant: 'destructive',
    });
  }
}

function ResultRow({
  medicine,
  onAdd,
  onAddSubstitute,
}: {
  medicine: CartableMedicine;
  onAdd: () => void;
  /** Fetches the substitute's full record (real batch/expiry, not a placeholder — this goes on a printed bill) and adds it. */
  onAddSubstitute: (id: string) => void;
}) {
  const expiringSoon = daysToExpiryOf(medicine) <= 90;
  const outOfStock = medicine.stock === 0;
  const [showSubstitutes, setShowSubstitutes] = useState(false);
  const substitutes = useSubstitutes(outOfStock && showSubstitutes ? medicine.id : undefined);

  if (outOfStock) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-2">
        <button
          type="button"
          onClick={() => setShowSubstitutes((v) => !v)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-muted-foreground">{medicine.name}</p>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-status-out">
              <PackageX className="h-3 w-3" /> Out of stock
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
            Substitutes
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showSubstitutes && 'rotate-180')} />
          </div>
        </button>

        {showSubstitutes && (
          <div className="mt-2 space-y-1 border-t border-border pt-2">
            {substitutes.isLoading ? (
              <p className="py-2 text-center text-xs text-muted-foreground">Looking for substitutes…</p>
            ) : !substitutes.data || substitutes.data.data.length === 0 ? (
              <p className="py-2 text-center text-xs text-muted-foreground">
                No substitutes found — nothing shares this composition, and none are linked yet.
              </p>
            ) : (
              substitutes.data.data.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between gap-2 rounded bg-secondary/50 px-2 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium">{sub.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {sub.stock} {sub.unit} in stock · {money(sub.price)}
                      {sub.source === 'manual' ? ' · Linked' : ' · Same composition'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={sub.stock === 0}
                    onClick={() => onAddSubstitute(sub.id)}
                  >
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onAdd}
      className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-secondary"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{medicine.name}</p>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{medicine.stock} {medicine.unit} in stock</span>
          <span>·</span>
          <span>Batch {medicine.batchNo}</span>
          {expiringSoon && (
            <Badge variant="expiring" className="px-1.5 py-0 text-[10px]">
              <AlertTriangle className="h-2.5 w-2.5" />
              {daysToExpiryOf(medicine)}d
            </Badge>
          )}
        </div>
      </div>
      <span className="tabular shrink-0 text-sm font-semibold">{money(medicine.price)}</span>
    </button>
  );
}

/**
 * Search + quick-add for the billing screen. Client-side ranked search over a
 * cached catalogue (instant, no per-keystroke request), plus keyboard-wedge
 * barcode scanning and a frequently-sold shortcut list for when the search
 * box is empty.
 */
export function MedicineSearch() {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 150);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastKeystrokeAt = useRef(0);
  const scanBuffer = useRef('');

  const { data: catalogue, isLoading: catalogueLoading } = useBillingCatalogue();
  const { data: frequentlySold } = useFrequentlySold(8);
  const barcodeLookup = useBarcodeLookup();

  // Voice just transcribes into the same query state a typed search would —
  // the existing ranked search below does the actual matching, so there's no
  // separate fuzzy-match logic to keep in sync with searchMedicines.
  const voice = useSpeechToText((transcript) => {
    setQuery(transcript);
    toast({ title: `Heard: "${transcript}"` });
  });

  const results = useMemo(
    () => (catalogue ? searchMedicines(catalogue, debouncedQuery) : []),
    [catalogue, debouncedQuery],
  );

  function handleAdd(medicine: CartableMedicine) {
    addMedicineToCart(medicine);
    setQuery('');
    inputRef.current?.focus();
  }

  // The substitutes list doesn't carry batch/expiry — a full record is
  // fetched at add-time so what lands on the printed bill is real, not a
  // placeholder.
  async function handleAddSubstitute(id: string) {
    try {
      const medicine = await api.get<Medicine>(`/api/medicines/${id}`);
      handleAdd(medicine);
    } catch (err) {
      toast({
        title: 'Could not add substitute',
        description: err instanceof ApiError ? err.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function lookupBarcode(code: string) {
    try {
      const medicine = await barcodeLookup.mutateAsync(code);
      handleAdd(medicine);
    } catch (error) {
      if (isBarcodeNotFound(error)) {
        toast({ title: 'No match', description: `No medicine found for barcode ${code}.` });
      } else {
        toast({ title: 'Barcode lookup failed', variant: 'destructive' });
      }
    }
  }

  // Detects a hardware scanner by keystroke timing rather than requiring a
  // separate "scan mode" toggle — the pharmacist just scans, into the same box
  // they'd otherwise type a name into.
  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (document.activeElement !== inputRef.current) return;

      const now = performance.now();
      const gap = now - lastKeystrokeAt.current;
      lastKeystrokeAt.current = now;

      if (event.key === 'Enter') {
        const buffered = scanBuffer.current;
        scanBuffer.current = '';
        if (buffered.length >= SCANNER_MIN_LENGTH) {
          event.preventDefault();
          setQuery('');
          void lookupBarcode(buffered);
        }
        return;
      }

      if (event.key.length === 1) {
        scanBuffer.current = gap < SCANNER_KEYSTROKE_MS ? scanBuffer.current + event.key : event.key;
      }
    }

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (voice.error) toast({ title: voice.error, variant: 'destructive' });
  }, [voice.error]);

  const showResults = debouncedQuery.trim().length > 0;

  return (
    <Card className="flex h-full flex-col">
      <div className="border-b border-border p-4">
        <div className="relative">
          {barcodeLookup.isPending ? (
            <LoaderCircle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : (
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          )}
          <Input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search medicine, or scan a barcode…"
            className={cn('h-12 pl-9 text-base', voice.supported ? 'pr-16' : 'pr-9')}
          />
          {voice.supported && (
            <button
              type="button"
              onClick={voice.toggle}
              title="Voice search"
              aria-label={voice.listening ? 'Stop voice search' : 'Voice search'}
              className={cn(
                'absolute right-9 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground',
                voice.listening && 'animate-pulse text-destructive',
              )}
            >
              <Mic className="h-4 w-4" />
            </button>
          )}
          <Barcode className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {catalogueLoading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : showResults ? (
          results.length > 0 ? (
            <div className="space-y-0.5">
              {results.map((medicine) => (
                <ResultRow
                  key={medicine.id}
                  medicine={medicine}
                  onAdd={() => handleAdd(medicine)}
                  onAddSubstitute={handleAddSubstitute}
                />
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No matches for "{debouncedQuery}".
            </p>
          )
        ) : (
          <div className="p-2">
            <p className="mb-2 flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Frequently sold
            </p>
            {frequentlySold && frequentlySold.length > 0 ? (
              <div className={cn('grid grid-cols-2 gap-2 sm:grid-cols-3')}>
                {frequentlySold.map(({ medicine }) => (
                  <button
                    key={medicine.id}
                    type="button"
                    onClick={() => handleAdd(medicine)}
                    disabled={medicine.stock === 0}
                    className="flex flex-col items-start gap-0.5 rounded-md border border-border p-2.5 text-left transition-colors hover:border-primary/40 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span className="line-clamp-2 text-xs font-medium">
                      {medicine.shortName || medicine.name}
                    </span>
                    <span className="tabular text-xs text-muted-foreground">
                      {money(medicine.price)}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Search above to add items to the bill.
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
